<template>
    <div>
        <DCard
            padding
            class="overflow-auto"
        >
            <DCol>
                <DRow
                    class="mb-3 d-flex justify-content-end"
                    align-vertical="end"
                >
                    <DCol
                        sm="2"
                    >
                        <DButton
                            id="table-send-invoices"
                            :label="__('customers.invoices.send_invoice_archive.title')"
                            class="float-end"
                            @click="showInvoiceArchiveConfirmationModal = true"
                        />
                    </DCol>
                    <DCol
                        sm="2"
                    >
                        <DButton
                            id="table-send-open-invoices"
                            :label="__('customers.invoices.open_invoices_mail.title')"
                            class="float-end"
                            @click="showOpenInvoicesMailConfirmationModal = true"
                        />
                    </DCol>
                    <DCol
                        sm="2"
                    >
                        <DButton
                            id="table-configuration"
                            icon="cog-outline"
                            :label="__('messages.list_configuration')"
                            class="float-end"
                            @click="showListViewModal = true"
                        />
                    </DCol>
                </DRow>
                <DRow
                    class="mb-3"
                    align-vertical="end"
                >
                    <DCol sm="2">
                        <DDateFormatInput
                            id="date-from"
                            v-model="filterData.search_issue_date_from"
                            format="YYYY-MM-DD"
                            type="date"
                            :have-class="'mb-0'"
                            :placeholder="__('customers.invoices.search_issue_date_placeholder')"
                            :label="__('customers.invoices.search_issue_date_from')"
                            add-input-classes="bg-light"
                            class="mb-0"
                            @keydown.enter.exact.prevent="changePage(1)"
                        />
                    </DCol>

                    <DCol sm="2">
                        <DDateFormatInput
                            id="date-to"
                            v-model="filterData.search_issue_date_to"
                            format="YYYY-MM-DD"
                            type="date"
                            :have-class="'mb-0'"
                            :label="__('customers.invoices.search_issue_date_to')"
                            add-input-classes="bg-light"
                            class="mb-0"
                            @keydown.enter.exact.prevent="changePage(1)"
                        />
                    </DCol>

                    <DCol sm="2">
                        <DMultiSelect
                            id="payment_state"
                            :options="paymentStateOptions"
                            :label="__('customers.invoices.status')"
                            :placeholder="__('customers.invoices.filter_state')"
                            :selection="true"
                            :selected="selectedStatuses"
                            selection-type="text"
                            @update="updateSelectedPaymentStates"
                        />
                    </DCol>

                    <DCol sm="2">
                        <DInput
                            id="net-amount-from"
                            v-model="filterData.net_amount_from"
                            type="number"
                            :label="__('customers.invoices.net_amount_from')"
                            :placeholder="__('customers.invoices.amount_placeholder')"
                            class="mb-0"
                            @keydown.enter.exact.prevent="changePage(1)"
                        />
                    </DCol>

                    <DCol sm="2">
                        <DInput
                            id="net-amount-to"
                            v-model="filterData.net_amount_to"
                            type="number"
                            :label="__('customers.invoices.net_amount_to')"
                            :placeholder="__('customers.invoices.amount_placeholder')"
                            class="mb-0"
                            @keydown.enter.exact.prevent="changePage(1)"
                        />
                    </DCol>

                    <DCol sm="2">
                        <DInput
                            id="contract-id"
                            v-model="filterData.contract_id"
                            type="number"
                            :label="__('customers.invoices.contract_id')"
                            :placeholder="__('customers.invoices.contract_id')"
                            class="mb-0"
                        />
                    </DCol>

                    <DCol
                        sm="2"
                        class="d-flex gap-2 justify-content-end pt-3"
                    >
                        <DButton
                            :label="__('messages.actions.filter')"
                            @click="changePage(1)"
                        />
                        <DButton
                            icon="filter-off"
                            :label="__('messages.actions.reset_filter')"
                            @click="resetFilters"
                        />
                    </DCol>

                    <DCol
                        v-if="filterData.contract"
                        sm="2"
                        class="pt-1"
                    >
                        <div class="filter__tag">
                            <DTag
                                color="success"
                                :label="`${__('customers.invoices.filter_contract')} #${filterData.contract}`"
                            />
                        </div>
                    </DCol>
                </DRow>
            </DCol>
            <DRow class="mt-3">
                <DCol>
                    <!-- Wrapper div, because `table-responsive` on DTable does nothing: app.scss
                         forces `overflow: visible !important` on it so row dropdowns aren't clipped. -->
                    <div class="overflow-x-auto">
                        <DTable
                            :items="invoices.items"
                            :fields="getFields"
                            :sorter="isSortable"
                            :sorter-value="sorterValue"
                            class="table-ellipsis-text mt-3"
                            @update:sorter-value="updateOrdering"
                        >
                            <template #id="{item}">
                                <td>
                                    <Link
                                        v-if="!checkIfCreditNoteHavePermission(item)"
                                        :href="getShowLink(item)"
                                    >
                                        {{ item.id }}
                                    </Link>
                                    <span v-else>
                                        {{ item.id }}
                                    </span>
                                </td>
                            </template>
                            <template #line_items="{item}">
                                <td>
                                    {{ getInvoiceLineItems(item, maxLineItemsDisplayed) }}
                                </td>
                            </template>
                            <template #incident="{item}">
                                <td>
                                    <IncidentFlag
                                        v-if="userPermissions.includes('customer.incidents_view') && item.has_incidents"
                                        id="invoices-incident-flag"
                                        :active-incident="item.has_open_incidents"
                                        :relation-type="'invoices'"
                                        :relation-id="item.id"
                                        :customer-id="item.customer_id"
                                    />
                                </td>
                            </template>
                            <template #type="{item}">
                                <td>
                                    {{ __('customers.contacts.invoice_type.' + item.type) }}
                                    <template v-if="item.canceled_invoice_id">
                                        <br>
                                        <span class="text-muted small">
                                            {{ item.type === 'invoice_cancellation'
                                                ? __('customers.invoices.for_invoice')
                                                : __('customers.invoices.for_credit_note')
                                            }}
                                            {{ ' ' + item.canceled_invoice_id }}
                                        </span>
                                    </template>
                                </td>
                            </template>
                            <template #total_amount="{item}">
                                <td
                                    :class="{'table-text-color-red': item.total_amount < 0}"
                                >
                                    {{ priceFormat(item.total_amount, 2, customer.market_region_id) }}
                                </td>
                            </template>
                            <template #open_item_amount="{item}">
                                <td
                                    v-if="item.open_item_amount !== '' && item.open_item_amount !== null"
                                    :class="{'table-text-color-red': item.open_item_amount < 0}"
                                >
                                    {{ roundPrice(item.open_item_amount) }}
                                </td>
                                <td v-else />
                            </template>
                            <template #dunning_status="{item}">
                                <td>
                                    <div v-if="item.dunning_status && item.in_lawsuit === false">
                                        {{ __('settings.dunning_pdf_settings.types.' + item.dunning_status) }}
                                    </div>
                                    <div v-if="item.dunning_status && item.in_lawsuit === true">
                                        {{ __('customers.invoices.in_lawsuit') }}
                                    </div>
                                    <strong v-if="item.sepa_chargeback === true">
                                        <br>
                                        {{ __('customers.invoices.chargeback') }}
                                    </strong>
                                    <strong v-if="item.debt_loss === true">
                                        <br>
                                        {{ __('customers.invoices.debt_loss') }}
                                    </strong>
                                </td>
                            </template>
                            <template #status="{item}">
                                <td>{{ __('customers.contacts.invoice_status.' + item.status) }}<span v-if="item.prepaid"> ({{ __('customers.contacts.invoice_status.prepaid') }})</span> </td>
                            </template>
                            <template #date="{item}">
                                <td> {{ setDateFormatEuropeBerlin(item.date) }} </td>
                            </template>
                            <template #due_date="{item}">
                                <td> {{ setDateFormatEuropeBerlin(item.due_date) }} </td>
                            </template>
                            <template #net_amount="{item}">
                                <td
                                    class="text-right"
                                    :class="{'table-text-color-red': item.net_amount < 0}"
                                >
                                    {{ roundPrice(item.net_amount) }}
                                </td>
                            </template>
                            <template #amount_to_pay="{item}">
                                <td
                                    class="text-right"
                                    :class="{'table-text-color-red': item.amount_to_pay < 0}"
                                >
                                    {{ roundPrice(item.amount_to_pay) }}
                                </td>
                            </template>
                            <template #products="{item}">
                                <td>
                                    <span
                                        v-for="(product, index) in item.products"
                                        :key="index"
                                    >
                                        {{ product.name }}<span v-if="item.products.length !== index + 1">, </span>
                                    </span>
                                </td>
                            </template>
                            <template #contracts="{item}">
                                <td>
                                    <span
                                        v-for="(contract, index) in item.contracts"
                                        :key="index"
                                    >
                                        <Link :href="contractUrl(contract)">
                                            {{ contract.id }}
                                        </Link><span v-if="item.contracts.length !== index + 1">, </span>
                                    </span>
                                </td>
                            </template>
                            <template #icon="{item}">
                                <td>
                                    <DIcon
                                        v-c-tooltip="getInvoiceItems(item)"
                                        name="cash"
                                        class="pointer-events"
                                        :size="30"
                                    />
                                </td>
                            </template>
                            <template #payment_term="{item}">
                                <td>
                                    {{ item.payment_term ?? '' }}
                                </td>
                            </template>
                            <template #prompt_cash_discount="{item}">
                                <td>
                                    {{ item.prompt_cash_discount ?? '' }}
                                </td>
                            </template>
                            <template #installments="{item}">
                                <td>
                                    {{ item.installments !== null && item.installments !== 0 ? item.installments + ' ' + __('customers.invoices.installments.installments') : '' }}
                                </td>
                            </template>
                            <template #actions="{item}">
                                <td>
                                    <DDropdown
                                        v-if="!checkIfCreditNoteHavePermission(item)"
                                        :id="`actions_dropdown_${item.id}`"
                                        color="secondary"
                                        add-menu-classes="pt-0 pb-0"
                                        :toggler-text="__('messages.actions.actions')"
                                        :size="'sm'"
                                    >
                                        <DDropdownItem
                                            v-if="item.type.startsWith('invoice') && showCancelButton(item) && userPermissions.includes('invoice.cancel')"
                                            :id="`action_cancel_partially_${item.id}`"
                                            @click="$inertia.get(cancelItemsRoute(item))"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="close"
                                                    :size="17"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __('customers.invoices.cancel_partially') }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="showCancelButton(item) && userPermissions.includes('invoice.cancel')"
                                            :id="`action_cancel_${item.id}`"
                                            @click="showCancelInvoiceModal(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="close"
                                                    :size="17"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __('customers.invoices.cancel') }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            :id="`action_download_${item.id}`"
                                            :href="getDownloadInvoiceLink(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="download"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ item.type.startsWith('invoice')
                                                        ? __('customers.invoices.invoice')
                                                        : __('customers.credit_notes.credit_note')
                                                    }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.has_e_invoice"
                                            :id="`action_download_e_invoice_xml_${item.id}`"
                                            :href="getDownloadEInvoiceXmlLink(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="download"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __('customers.invoices.e_invoice_xml') }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-for="(dunning, index) in item.dunning_letters"
                                            :id="`dunning_download_${item.id}_${dunning.name}`"
                                            :key="index"
                                            :href="getDownloadDunningLink(item, dunning.id)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="download"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ dunning.name }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.type.startsWith('invoice') && customer.invoice_csv"
                                            :id="`action_download_csv_${item.id}`"
                                            :href="getDownloadInvoiceCsvLink(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="download"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __('customers.invoices.invoice_csv') }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.type.startsWith('invoice') && item.hasPartnerSubContractsCsv"
                                            :id="`action_download_partner_csv_${item.id}`"
                                            :href="getDownloadInvoicePartnersSubContractsCsvLink(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="download"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __('customers.invoices.invoice_partner_subcontracts_csv') }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.type.startsWith('invoice') && userPermissions.includes('invoice.resend_document')"
                                            :id="`action_resend_${item.id}`"
                                            :href="resendInvoiceEmail(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="redo"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __(('tools.invoices.resend_invoice.' + item.customer_delivery_type)) }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.type.startsWith('invoice')
                                                && userPermissions.includes('invoice.manage')
                                                && item.manual_payment_available === true"
                                            :id="`action_trigger_payment_${item.id}`"
                                            @click="triggerPayment(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="plus"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __(('tools.invoices.trigger_payment')) }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            :id="`transaction_page_${item.id}`"
                                            @click="transactionPageLink(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="redo"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __(('customers.invoices.transaction_link')) }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.type.startsWith('invoice')
                                                && userPermissions.includes('invoice.manage')
                                                && item.status !== 'paid'
                                                && hasAlternativePaymentMethods(item)"
                                            :id="`change_payment_method_${item.id}`"
                                            @click="openChangePaymentMethodModal(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="credit-card-outline"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __(('customers.invoices.change_payment_method')) }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.type.startsWith('invoice')
                                                && item.status === 'unpaid'
                                                && userPermissions.includes('invoice.set_debt_loss')
                                                && !item.debt_loss"
                                            :id="`set_debt_loss_${item.id}`"
                                            @click="openSetDebtLossModal(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="delete"
                                                    :size="18"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __(('tools.invoices.set_debt_loss')) }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="item.isRefundApplicable === true && (userPermissions.includes('credit_balance.refund_limited') || userPermissions.includes('credit_balance.refund_unlimited'))"
                                            :id="`action_refund_${item.id}`"
                                            @click="showRefundModal(item)"
                                        >
                                            <div class="row pl-1 pr-1">
                                                <DIcon
                                                    name="credit-card-outline"
                                                    :size="17"
                                                    class="pr-1"
                                                />
                                                <div>
                                                    {{ __('customers.invoices.refund.title') }}
                                                </div>
                                            </div>
                                        </DDropdownItem>
                                    </DDropdown>
                                </td>
                            </template>
                        </DTable>
                    </div>
                    <DPagination
                        v-if="invoices.last_page > 1"
                        :active-page="invoices.current_page"
                        :pages="invoices.last_page"
                        align="center"
                        @update:activePage="changePage"
                    />
                </DCol>
            </DRow>
            <DFabs>
                <DFab
                    v-if="userPermissions.includes('invoice.view') && hasPendingInvoiceItems === true"
                    id="pending-invoice-items"
                    icon="eye"
                    color="secondary"
                    :label="__('customers.invoices.pending_invoice_items.pending_invoice_items')"
                    @click="$inertia.get($route('customers.customers.invoices.pending-invoice-items', {customer: customer.id}))"
                />
                <s-s-o-fab
                    :customer="customer"
                    @open-modal="showCreateCloudpitCustomerModal = true"
                    @close-modal="showCreateCloudpitCustomerModal = false"
                />
                <DFab
                    v-if="creditNoteCreationAvailable"
                    id="btn-add-credit-note"
                    icon="plus"
                    :label="__('customers.credit_notes.create_manually')"
                    @click="$inertia.get($route('customers.customers.credit-notes.create', {customer: customer.id}))"
                />
                <DFab
                    v-if="userPermissions.includes('invoice.create')"
                    id="btn-add-invoice"
                    icon="plus"
                    :label="__('customers.invoices.create_manually')"
                    @click="$inertia.get($route('customers.customers.invoices.create', {customer: customer.id}))"
                />
                <DFab
                    v-if="userPermissions.includes('invoice.view')"
                    id="btn-preview-invoice"
                    icon="eye"
                    color="secondary"
                    @click="requestPreview()"
                />
            </DFabs>

            <DModal
                v-model="activity_log_comment"
                v-model:show="showConfirmationModal"
                :title="cancellationTitle"
                :message="cancellationMessage"
                is-confirm
                :is-awaiting-validation="true"
                :show-change-reason-input="true"
                is-error
                :show-incident-list="true"
                :customer-id="customer?.id ?? 0"
                @setIncident="setIncident"
                @close="showConfirmationModal = false"
                @confirm="cancelCallBack"
            />
        </DCard>
        <DModal
            :show="showNextInvoiceModal"
            :title="__('customers.invoices.preview_invoice')"
            is-big
            @close="closeModal"
        >
            <template
                v-if="vatValid === false"
                #header
            >
                <span class="text-danger">{{ __('customers.invoices.vat_invalid') }}</span>
            </template>
            <DRow>
                <DCol md="12">
                    <DTabs>
                        <DTabItem
                            v-for="(pdf, index) in nextInvoicePdfs"
                            :key="index"
                            :label="__('customers.invoices.invoice') + pdf.groupName"
                            :active="currentTab === index"
                            @click="changeTab(index, pdf)"
                        />
                        <DTabItem
                            v-if="showCreditNoteTab === true"
                            :label="__('customers.credit_notes.credit_note')"
                            :active="currentTab === creditNoteTab"
                            @click="changeTab(creditNoteTab)"
                        />
                    </DTabs>
                    <DAlert
                        v-if="error"
                        class="m-0"
                        color="info"
                    >
                        {{ error }}
                    </DAlert>
                    <DCard
                        v-else
                        class="pt-0"
                    >
                        <DCardBody
                            v-if="currentTab !== creditNoteTab && invoicePdfSource !== null"
                        >
                            <div
                                v-if="nextInvoicePdfs.length > 1 && (showCreditNoteTab === false || nextInvoicePdfs.length !== 2)"
                                class="mb-2"
                            >
                                <div
                                    v-for="(value, key) in nextInvoicePdfs[currentTab].splitFactors"
                                    :key="key"
                                >
                                    <b>{{ __(`customers.invoices.split_factors.${key}`) }}</b>: {{ value }}
                                </div>
                            </div>
                            <pdf
                                v-for="page in invoicePdfNumPage"
                                :key="`invoice-page-${page}-${invoicePdfSource}`"
                                :src="invoicePdfSource"
                                :page="page"
                            />
                        </DCardBody>
                        <DCardBody v-if="currentTab === creditNoteTab && showCreditNoteTab === true && creditNotePdfSource !== null">
                            <pdf
                                v-for="i in creditNotePdfNumPages"
                                :key="`credit-note-page-${i}-${creditNotePdfSource}`"
                                :src="creditNotePdfSource"
                                :page="i"
                            />
                        </DCardBody>
                    </DCard>
                </DCol>
            </DRow>
            <template #actions>
                <DButton
                    v-if="currentTab !== creditNoteTab && downloadButton === true"
                    :label="__('customers.invoices.create_invoice')"
                    class="ms-1"
                    color="danger"
                    :disabled="isInLoadingState"
                    @click="showConfirmCreateInvoiceFromPreviewModal = true"
                />
                <DButton
                    v-if="showDistributorDownloadBtn === true"
                    :label="__('customers.invoices.csv_download')"
                    class="ms-1"
                    color="secondary"
                    :disabled="isInLoadingState"
                    @click="downloadCSVSampleDocument()"
                />
                <DButton
                    v-if="currentTab !== creditNoteTab && downloadButton === true"
                    :label="__('tools.documents.modal.download')"
                    class="ms-1"
                    color="success"
                    :disabled="isInLoadingState"
                    @click="downloadInvoiceSampleDocument()"
                />
                <DButton
                    v-if="creditNotePdfSource !== null && currentTab === creditNoteTab && showCreditNoteTab === true"
                    :label="__('tools.documents.modal.download')"
                    class="ms-1"
                    color="success"
                    :disabled="isInLoadingState"
                    @click="downloadCreditNoteSampleDocument()"
                />
            </template>
        </DModal>
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
        <DModal
            :show="showConfirmCreateInvoiceFromPreviewModal"
            :title="__('customers.invoices.confirm_crete_invoice_from_preview')"
            is-big
            @close="closeConfirmModal"
        >
            <template #actions>
                <DButton
                    v-if="currentTab !== creditNoteTab && downloadButton === true"
                    :label="__('customers.invoices.create_invoice_confirm')"
                    class="ms-1"
                    color="success"
                    :disabled="isInLoadingState"
                    @click="createInvoice()"
                />
            </template>
        </DModal>
        <ChangePaymentMethod
            v-if="showChangePaymentMethodModal"
            :show-modal="showChangePaymentMethodModal"
            :invoice="selectedInvoice"
            :payment-methods="paymentMethods"
            @close-modal="closeChangePaymentMethodModal()"
        />
        <InvoicePreviewModal
            v-if="showInvoicePreviewRequestModal"
            :customer="customer"
            :show-modal="showInvoicePreviewRequestModal"
            :internal-support-link="internalSupportLink"
            @close-modal="showInvoicePreviewRequestModal = false"
            @preview-ready="previewReady"
        />
        <DModal
            v-if="showInvoiceArchiveConfirmationModal"
            v-model:show="showInvoiceArchiveConfirmationModal"
            :title="__('customers.invoices.send_invoice_archive.title')"
            :message="__('customers.invoices.send_invoice_archive.message', {
                'customer_id': customer.id ?? '',
                'email': customer.email ?? '',
            })"
            is-confirm
            is-error
            @confirm="submitDownloadInvoiceArchiveConfirmation"
            @close="showInvoiceArchiveConfirmationModal = false"
        />
        <DModal
            v-if="showOpenInvoicesMailConfirmationModal"
            v-model:show="showOpenInvoicesMailConfirmationModal"
            :title="__('customers.invoices.open_invoices_mail.title')"
            :message="__('customers.invoices.open_invoices_mail.message', {
                'customer_id': customer.id ?? '',
                'email': customer.email ?? '',
            })"
            is-confirm
            is-error
            @confirm="submitOpenInvoicesMailConfirmation"
            @close="showOpenInvoicesMailConfirmationModal = false"
        />
        <SetDebtLossModal
            v-if="showSetDebtLossModal"
            :show-modal="showSetDebtLossModal"
            :invoice="selectedInvoice"
            @close-modal="closeSetDebtLossModal()"
        />
        <DModal
            v-if="showConfirmationRefundModal"
            v-model:show="showConfirmationRefundModal"
            :title="__('customers.invoices.refund.title')"
            :message="__('customers.invoices.refund.message')"
            is-confirm
            is-error
            @confirm="submitRefundInvoice(refundInvoice)"
            @close="cancelRefundInvoice"
        />
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import pdf from 'vue3-pdf'

import InvoicePreviewModal from '~@/Components/Customers/Invoices/InvoicePreviewModal'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'
import priceFormat from '~@/Mixins/priceFormat'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import ChangePaymentMethod from '~@/Components/Customers/Invoices/ChangePaymentMethod.vue'
import SetDebtLossModal from '~@/Components/Customers/Invoices/SetDebtLossModal.vue'

export default {
    name: 'CustomerInvoiceIndex',
    components: {
        SetDebtLossModal,
        ChangePaymentMethod,
        ListViewModal,
        CreateCloudPitCustomerModal,
        SSOFab,
        pdf,
        IncidentFlag,
        InvoicePreviewModal
    },
    mixins: [priceFormat],
    layout: [App, CustomerBase],
    props: {
        paymentStates: {
            type: Array,
            default: () => { return [] }
        },
        invoices: {
            type: Object,
            default: () => { return {} }
        },
        filters: {
            type: Object,
            default: () => {
                return {
                    contract: null,
                    search_payment_state: {},
                    search_issue_date_from: null,
                    search_issue_date_to: null,
                    order_by: '',
                    order_by_asc: false,
                    net_amount_from: null,
                    net_amount_to: null,
                    contract_id: null
                }
            }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        creditNoteCreationPossible: {
            type: Boolean,
            default: false
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        paymentMethods: {
            type: Object,
            default: () => { return {} }
        },
        internalSupportLink: {
            type: String,
            default: () => { return '' }
        },
        hasPendingInvoiceItems: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            showConfirmationModal: false,
            cancelCallBack: () => {
                return {}
            },
            showNextInvoiceModal: false,
            showConfirmCreateInvoiceFromPreviewModal: false,
            invoicePdfSource: null,
            invoiceFileName: null,
            error: '',
            nextInvoiceLoading: false,
            showCreateCloudpitCustomerModal: false,
            cancellationTitle: '',
            cancellationMessage: '',
            activity_log_comment: '',
            creditNotePdfSource: null,
            creditNotePdfNumPages: null,
            creditNoteFileName: null,
            currentTab: 0,
            showCreditNoteTab: false,
            showDistributorDownloadBtn: false,
            vatValid: true,
            filterData: {
                contract: this.filters.contract,
                search_payment_state: this.filters.search_payment_state,
                search_issue_date_from: this.filters.search_issue_date_from,
                search_issue_date_to: this.filters.search_issue_date_to,
                order_by: this.filters.order_by,
                order_by_asc: this.filters.order_by_asc ? 1 : 0,
                net_amount_from: this.filters.net_amount_from,
                net_amount_to: this.filters.net_amount_to,
                contract_id: this.filters.contract_id
            },
            nextInvoicePdfs: [],
            creditNoteTab: 1,
            downloadButton: true,
            showListViewModal: false,
            incident_id: null,
            showChangePaymentMethodModal: false,
            selectedInvoice: {},
            showInvoicePreviewRequestModal: false,
            showInvoiceArchiveConfirmationModal: false,
            showSetDebtLossModal: false,
            showOpenInvoicesMailConfirmationModal: false,
            showConfirmationRefundModal: false,
            refundInvoice: null,
            maxLineItemsDisplayed: 10,
        }
    },
    computed: {
        creditNoteCreationAvailable () {
            return this.creditNoteCreationPossible && this.$page.props.auth.permissions.includes('credit_note.manage')
        },
        invoicePdfNumPage () {
            return this.nextInvoicePdfs[this.currentTab].numPages
        },
        paymentStateOptions () {
            const states = this.paymentStates

            for (const index in states) {
                if (states[index].text.length !== 0) {
                    states[index].text = this.__(`customers.contacts.invoice_status.${states[index].value}`)
                }
                if (this.selectedStatuses.includes(states[index].value)) {
                    states[index].selected = true
                }
            }

            return states
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        sorterValue () {
            return {
                column: this.filterData.order_by,
                asc: !!this.filters.order_by_asc
            }
        },
        isSortable () {
            return { external: true }
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: item.column !== 'actions' && item.column !== 'icon' ? this.__('settings.list_view.' + item.list + '.' + item.column) : '',
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            return availableList
        },
        invoiceDecimals () {
            return this.$page.props.marketRegions[this.customer.market_region_id]?.decimal_places ?? 2
        },
        selectedStatuses () {
            if (!this.filterData.search_payment_state || this.filterData.search_payment_state.length === 0) return []

            return this.filterData.search_payment_state.map((item) => item)
        }
    },
    methods: {
        changeTab (tabIndex, pdfInfo = null) {
            if (pdfInfo !== null) {
                this.invoicePdfSource = pdfInfo.invoicePdfSource
                this.invoiceFileName = pdfInfo.invoiceFileName
            }

            this.currentTab = tabIndex
        },
        showCancelInvoiceModal (invoice) {
            if (invoice.type.startsWith('invoice')) {
                this.cancellationTitle = this.__('customers.invoices.confirm_title')
                this.cancellationMessage = this.__('customers.invoices.confirm_message')
                this.cancelCallBack = () => {
                    this.$inertia.post(this.$route('customers.customers.invoices.cancel', {
                        customer: this.customer.id,
                        invoice: invoice.id
                    }), {
                        activity_log_comment: this.activity_log_comment,
                        incident_id: this.incident_id
                    }, {
                        onSuccess: () => {
                            this.showConfirmationModal = false
                        }
                    })
                }
            } else {
                this.cancellationTitle = this.__('customers.credit_notes.confirm_title')
                this.cancellationMessage = this.__('customers.credit_notes.confirm_message')

                this.cancelCallBack = () => {
                    this.$inertia.post(this.$route('customers.customers.credit-notes.cancel', {
                        customer: this.customer.id,
                        credit_note: invoice.id
                    }), {
                        activity_log_comment: this.activity_log_comment
                    }, {
                        onSuccess: () => {
                            this.showConfirmationModal = false
                        }
                    })
                }
            }

            this.showConfirmationModal = true
        },
        showCancelButton (invoice) {
            if (invoice.type.startsWith('invoice')) {
                return this.userPermissions.includes('invoice.cancel') &&
                    invoice.type !== 'invoice_cancellation' &&
                    invoice.active_item_exists
            }

            return this.userPermissions.includes('credit_note.manage') &&
                invoice.type !== 'credit_note_cancellation' &&
                invoice.active_item_exists
        },
        showRefundModal (item) {
            this.refundInvoice = item
            this.showConfirmationRefundModal = true
        },
        submitRefundInvoice (invoice) {
            this.$inertia.post(
                this.$route('customers.customers.invoices.refund', {
                    customer: this.customer.id,
                    invoice: invoice.id
                }),
                {},
                {
                    onSuccess: () => {
                    },
                    onError: () => {
                    },
                    onFinish: () => {
                        this.refundInvoice = null
                    }
                }
            )
        },
        cancelRefundInvoice () {
            this.refundInvoice = null
            this.showConfirmationRefundModal = false
        },
        cancelItemsRoute (invoice) {
            return this.$route(
                'customers.customers.invoices.items',
                { customer: this.customer.id, invoice: invoice.id }
            )
        },
        getDownloadInvoiceLink (invoice) {
            if (invoice.type.startsWith('invoice')) {
                return this.$route('tools.invoices.download', {
                    customer: this.customer.id,
                    invoice: invoice.id
                })
            }

            return this.$route('customers.customers.credit-notes.download', {
                customer: this.customer.id,
                credit_note: invoice.id
            })
        },
        getDownloadEInvoiceXmlLink (invoice) {
            if (invoice.type === 'invoice' || invoice.type === 'invoice_cancellation') {
                return this.$route('tools.invoices.download-einvoice-xml', {
                    invoice: invoice.id
                })
            }
            if (invoice.type === 'credit_note' || invoice.type === 'credit_note_cancellation') {
                return this.$route('tools.credit-notes.download-einvoice-xml', {
                    credit_note: invoice.id
                })
            }
        },
        getDownloadDunningLink (invoice, dunningId) {
            return this.$route('tools.dunning.download', { dunning: dunningId })
        },
        closeModal () {
            this.showNextInvoiceModal = false
            this.pdfSource = null
        },
        closeConfirmModal () {
            this.showConfirmCreateInvoiceFromPreviewModal = false
        },
        downloadInvoiceSampleDocument () {
            const link = document.createElement('a')
            link.href = this.invoicePdfSource
            link.download = this.invoiceFileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        createInvoice () {
            this.$inertia.post(
                this.$route('customers.customers.invoices.create_from_preview_next_invoice', {
                    customer: this.customer.id
                }),
                {},
                {
                    onSuccess: () => {
                        this.closeConfirmModal()
                        this.closeModal()
                    }
                }
            )
        },
        downloadCreditNoteSampleDocument () {
            const link = document.createElement('a')
            link.href = this.creditNotePdfSource
            link.download = this.creditNoteFileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        downloadCSVSampleDocument () {
            const link = document.createElement('a')
            link.href = this.$route('customers.customers.invoices.download_csv_sub_contracts', { customer: this.customer.id })
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        requestPreview () {
            this.showInvoicePreviewRequestModal = true
            window.axios.post(this.$route('customers.customers.invoices.request_preview', {
                customer: this.customer.id
            }))
        },
        async getFiles (path) {
            const response = await window.axios.get(this.$route('tools.temporary.file'), {
                params: {
                    path: path
                }
            })

            if (response.status !== 200) {
                this.error = response.error
            }

            return response.data
        },
        async previewReady ({ invoices, creditNote, hasPartnerItems, isVatValid, error }) {
            this.showInvoicePreviewRequestModal = false
            this.nextInvoiceLoading = true
            this.error = ''
            this.showNextInvoiceModal = false
            this.invoicePdfSource = null
            this.invoiceFileName = null
            this.creditNotePdfSource = null
            this.creditNoteFileName = null
            this.showCreditNoteTab = false
            this.showDistributorDownloadBtn = false
            this.currentTab = 0
            this.nextInvoicePdfs = []
            this.creditNoteTab = 1

            if (error !== null) {
                this.error = error
                this.downloadButton = false
                this.nextInvoiceLoading = false
                this.showNextInvoiceModal = true

                return
            }

            this.nextInvoiceLoading = false
            for (let i = 0; i < invoices.length; i++) {
                const file = await this.getFiles(invoices[i].temporaryPath ?? null)

                const invoiceFileName = invoices[i].filename
                const invoiceBlob = this.createBlob(file.content, invoices[i].mimetype)
                const invoicePdfSource = URL.createObjectURL(invoiceBlob)

                // Set param for download for first invoice
                if (this.nextInvoicePdfs.length === 0) {
                    this.invoicePdfSource = invoicePdfSource
                    this.invoiceFileName = invoiceFileName
                }

                // Store PDF info
                this.nextInvoicePdfs.push({
                    invoicePdfSource: invoicePdfSource,
                    invoiceFileName: invoiceFileName,
                    groupName: invoices[i].groupName ? ' - ' + invoices[i].groupName : '',
                    splitFactors: invoices[i].split_factors,
                    numPages: 1
                })

                // Finally load the real number of pages in the PDF
                pdf.createLoadingTask(invoicePdfSource)
                    .promise.then(pdf => {
                        const i = this.nextInvoicePdfs.findIndex(e => e.invoicePdfSource === invoicePdfSource)
                        this.nextInvoicePdfs[i].numPages = pdf.numPages
                    })
            }

            this.creditNoteTab = this.nextInvoicePdfs.length
            this.showDistributorDownloadBtn = hasPartnerItems
            this.vatValid = isVatValid
            this.downloadButton = true
            this.showNextInvoiceModal = true

            if (creditNote !== null) {
                this.showCreditNoteTab = true
                const creditNoteFile = await this.getFiles(creditNote.temporaryPath ?? null)

                const creditNoteBlob = this.createBlob(creditNoteFile.content, creditNote.mimetype)
                this.creditNotePdfSource = URL.createObjectURL(creditNoteBlob)
                pdf.createLoadingTask(this.creditNotePdfSource)
                    .promise.then(pdf => {
                        this.creditNotePdfNumPages = pdf.numPages
                    })
                this.creditNoteFileName = creditNote.filename
            }
        },
        createBlob (base64String, contentType) {
            const blobPdfFromBase64String = (base64String, contentType) => {
                const byteArray = Uint8Array.from(
                    atob(base64String)
                        .split('')
                        .map(char => char.charCodeAt(0))
                )
                return new Blob([byteArray], { type: contentType })
            }
            return blobPdfFromBase64String(base64String, contentType)
        },
        changePage (page) {
            const filters = this.filterData
            const query = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))

            if (page > 1) {
                query.page = page
            }

            this.$inertia.get(this.$route('customers.customers.invoices.index', {
                customer: this.customer.id,
                save_filters: true
            }), query)
        },
        resetFilters () {
            const filters = this.filterData
            filters.contract = null
            filters.search_payment_state = {}
            filters.search_issue_date_from = null
            filters.search_issue_date_to = null
            filters.net_amount_from = null
            filters.net_amount_to = null
            filters.contract_id = null
            const query = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            query.page = 1
            this.$inertia.get(this.$route('customers.customers.invoices.index', {
                customer: this.customer.id,
                save_filters: true,
                default_filters: true
            }), query)
        },
        getShowLink (item) {
            return item.type.startsWith('invoice')
                ? this.$route('customers.customers.invoices.show', { customer: this.customer.id, invoice: item.id })
                : this.$route('customers.customers.credit-notes.show', { customer: this.customer.id, credit_note: item.id })
        },
        checkIfCreditNoteHavePermission (item) {
            return !item.type.startsWith('invoice') && !this.$page.props.auth.permissions.includes('credit_note.manage')
        },
        getDownloadInvoiceCsvLink (invoice) {
            return this.$route('tools.invoices.download-csv', {
                customer: this.customer.id,
                invoice: invoice.id
            })
        },
        getDownloadInvoicePartnersSubContractsCsvLink (invoice) {
            return this.$route('tools.invoices.download-partner-subcontracts-csv', {
                customer: this.customer.id,
                invoice: invoice.id
            })
        },
        resendInvoiceEmail (invoice) {
            return this.$route('tools.invoices.resend', {
                invoice: invoice.id
            })
        },
        updateOrdering (event) {
            this.filterData.order_by = event.column
            this.filterData.order_by_asc = event.asc ? 1 : 0

            this.changePage(this.invoices.current_page)
        },
        contractUrl (contract) {
            return this.$route('customers.customers.contracts.show', { customer: contract.customer_id, contract: contract.id })
        },
        getInvoiceItems (invoice) {
            if (invoice.total_item_count === 0) {
                return this.__('customers.invoices.tooltip.no_items')
            }

            if (invoice.total_item_count > 20) {
                return this.__('customers.invoices.tooltip.too_many_items')
            }

            let tooltip = '<div class="d-flex gap-2">'
            tooltip += '<div style="width: 200px;"><strong>' + this.__('customers.invoices.tooltip.product') + '</strong></div>'
            tooltip += '<div style="width: 100px;"><strong>' + this.__('customers.invoices.tooltip.from_date') + '</strong></div>'
            tooltip += '<div style="width: 100px;"><strong>' + this.__('customers.invoices.tooltip.to_date') + '</strong></div>'
            tooltip += '<div style="width: 100px;"><strong>' + this.__('customers.invoices.tooltip.quantity') + '</strong></div>'
            tooltip += '<div style="width: 100px;"><strong>' + this.__('customers.invoices.tooltip.unit_price_net') + '</strong></div>'
            tooltip += '</div>'

            invoice.limited_items.forEach((item) => {
                let productText = item.product

                if (productText.length > 25) {
                    productText = productText.substring(0, 25).trim() + '...'
                }

                tooltip += '<div class="d-flex gap-2 pb-1">'
                tooltip += '<div style="width: 200px;">' + productText + '</div>'
                tooltip += '<div style="width: 100px;">' + this.setDateFormat(item.from_date) + '</div>'
                tooltip += '<div style="width: 100px;">' + this.setDateFormat(item.to_date) + '</div>'
                tooltip += '<div style="width: 100px;">' + item.quantity + '</div>'
                tooltip += '<div style="width: 100px;">' + this.roundPrice(item.net_amount) + '</div>'
                tooltip += '</div>'
            })

            return tooltip
        },
        getInvoiceLineItems (invoice, maxItems, separator = ', ') {
            let lineItems = invoice.limited_items
                .slice(0, maxItems)
                .map(item => item.product)
                .join(separator)

            if (invoice.limited_items.length > maxItems) {
                (lineItems += separator + '...')
            }

            return lineItems
        },
        triggerPayment (invoice) {
            return this.$inertia.post(this.$route('tools.invoices.trigger-payment', {
                invoice: invoice.id
            }), {})
        },
        setIncident (value) {
            this.incident_id = value
        },
        roundPrice (value) {
            if (value || value === 0.0) {
                return this.priceFormat(value, this.invoiceDecimals, this.customer.market_region_id)
            }

            return ''
        },
        transactionPageLink (invoice) {
            return this.$inertia.get(this.$route('customers.customers.transactions.index', {
                customer: this.customer.id
            }), {
                search: invoice.id ?? null
            })
        },
        openChangePaymentMethodModal (invoice) {
            this.selectedInvoice = invoice
            this.showChangePaymentMethodModal = true
        },
        hasAlternativePaymentMethods (item) {
            return this.paymentMethods.some((paymentMethod) => paymentMethod.value !== item.payment_method)
        },
        closeChangePaymentMethodModal () {
            this.selectedInvoice = {}
            this.showChangePaymentMethodModal = false
        },
        updateSelectedPaymentStates (values) {
            this.removeAllFilterStatusesData = values.length === 0

            this.filterData.search_payment_state = values
        },
        submitDownloadInvoiceArchiveConfirmation () {
            return this.$inertia.get(this.$route('customers.customers.invoices.send-archive-invoices', {
                customer: this.customer.id
            }))
        },
        openSetDebtLossModal (invoice) {
            this.selectedInvoice = invoice
            this.showSetDebtLossModal = true
        },
        closeSetDebtLossModal () {
            this.selectedInvoice = {}
            this.showSetDebtLossModal = false
        },
        submitOpenInvoicesMailConfirmation () {
            return this.$inertia.get(this.$route('customers.customers.invoices.send-open-invoices-mail', {
                customer: this.customer.id
            }))
        }
    }
}
</script>

<style lang="scss" scoped>
.filter__tag {
    padding: 8px;
}
</style>

<style lang="scss">
.tooltip-inner {max-width: unset !important}
// Solve configuration table column issue, when you set small width of columns th will not overlap
.table-ellipsis-text table th {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
